import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import KnowledgeGraph from '../components/KnowledgeGraph';
import { Network } from '@phosphor-icons/react';

interface GraphData {
  nodes: any[];
  edges: any[];
  clusters: any[];
}

interface KnowledgeDomain {
  domain: string;
  content_count: number;
  avg_difficulty: string;
  top_tags: string[];
  latest_content_date: string;
}

export default function KnowledgeGraphPage() {
  const { token } = useAuthStore();
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], edges: [], clusters: [] });
  const [knowledgeDomains, setKnowledgeDomains] = useState<KnowledgeDomain[]>([]);
  const [loading, setLoading] = useState(false); // Start false to prevent flash
  const [initialLoadDone, setInitialLoadDone] = useState(false); // Track first load
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGraphData = async () => {
      if (!token) {
        setInitialLoadDone(true);
        return;
      }

      try {
        // Only show loading on first load
        if (!initialLoadDone) {
          setLoading(true);
        }
        
        // Fetch semantic knowledge graph data
        const graphResponse = await fetch('/api/content/graph/data', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (graphResponse.ok) {
          const graphResult = await graphResponse.json();
          setGraphData(graphResult.data || { nodes: [], edges: [], clusters: [] });
        }

        // Fetch knowledge domains
        const domainsResponse = await fetch('/api/content/domains', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (domainsResponse.ok) {
          const domainsResult = await domainsResponse.json();
          setKnowledgeDomains(domainsResult.data || []);
        }

      } catch (err) {
        setError('Failed to load knowledge graph data');
        console.error('Error fetching graph data:', err);
      } finally {
        setLoading(false);
        setInitialLoadDone(true);
      }
    };

    fetchGraphData();
  }, [token]);

  const handleNodeClick = (node: any) => {
    // Navigation is handled by KnowledgeGraph component using React Router
    // This callback is just for any additional logic if needed
    console.log('Node clicked:', node.id);
  };


  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">{error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Minimal Header */}
      <div className="bg-gray-50 dark:bg-gray-900 p-6 md:p-8 flex-shrink-0">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                <Network weight="duotone" className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">Knowledge Graph</h1>
                <p className="text-gray-500 dark:text-gray-400">Visualize connections in your knowledge</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full-Screen Knowledge Graph */}
      <div className="flex-1 relative bg-gray-50 dark:bg-gray-900 overflow-hidden">
        <KnowledgeGraph 
          className="h-full w-full"
          onNodeClick={handleNodeClick}
        />
      </div>
    </div>
  );
}